// Gloves roster screens. Pure: data in, HTML out.
// Words: GLOVES_WORDS.md "Admin roster" (approved 2026-10-05), read with GLOVES_REWORK_WORDS.md
// (approved 2026-10-06). Roster screens: MOCKUP_GLOVES_APPROVED.html, with the rework's card words.
// The QEW records gloves on the glove card; the app runs the clocks. Enter Gloves lets the admin record
// a site's sealed pairs in one pass (GLOVES_ENTER_WORDS.md, 2026-10-24).

import { GLOVE_CLASS_LINE, GLOVE_SIZES, usDate } from "./gloves";
import { isFilled, saveLabel, type EnterCell, type EnterRow } from "./gloves-enter";
import { BUCKETS, bucketCounts, pastPairLine, rowStatus, type PersonGloves, type RosterBucket, type RosterRow } from "./gloves-roster";
import { esc, ident } from "./training-screens";

function row(label: string, value: string, end = ""): string {
  return `<div class="r"><div class="k">${esc(label)}</div><div class="v">${value}</div><div class="e">${end}</div></div>`;
}

const TONE_PILL: Record<string, string> = { ok: "green", warn: "amber", bad: "red", wait: "grey" };

function statusPill(r: RosterRow): string {
  const tone = r.locked ? "grey" : TONE_PILL[r.card?.dashTone ?? "wait"];
  return `<span class="pill ${tone}">${esc(rowStatus(r).toUpperCase())}</span>`;
}

export function glovesRoster(all: RosterRow[], siteNames: Record<string, string>, site: string, bucket: RosterBucket | null): string {
  const inSite = site === "all" ? all : all.filter((r) => r.siteNumber === site);
  const counts = bucketCounts(inSite);
  const shown = bucket ? inSite.filter((r) => r.bucket === bucket) : inSite;
  const sites = Object.keys(siteNames).sort();
  // A site dropdown, as Work Requests, Near Miss and the Email Log use. A tab per site ran off the screen at 47 sites.
  const options = [
    `<option value="all"${site === "all" ? " selected" : ""}>All Sites</option>`,
    ...sites.map((s) => `<option value="${esc(s)}"${site === s ? " selected" : ""}>${esc(`${s} · ${siteNames[s]}`)}</option>`),
  ].join("");
  const cells = BUCKETS.map(
    (b) => `<a class="cnt-cell gl-${b.tone}${bucket === b.key ? " on" : ""}" href="#/gloves/${site}${bucket === b.key ? "" : `/${b.key}`}"><b>${counts[b.key]}</b><small>${esc(b.label.toUpperCase())}</small></a>`,
  ).join("");
  const rows = shown
    .map(
      (r) => `<a class="ar gl-row" href="#/gloves/p/${encodeURIComponent(r.qewId)}">
        <span class="chip">${esc(r.qewId)}</span>
        <span class="t"><b>${esc(r.name)}</b><small>${esc([r.pairId ?? (r.locked ? "Opens after Nodes 1 to 3" : "No pair"), r.dueLine].filter(Boolean).join(" · "))}</small>${statusPill(r)}</span></a>`,
    )
    .join("");
  const title = site === "all" ? "All Sites" : siteNames[site] ?? `Site ${site}`;
  return `${ident("GL", `Gloves · ${title}`, `${inSite.length} active ${inSite.length === 1 ? "QEW" : "QEWs"} · ${GLOVE_CLASS_LINE}`)}
    <div class="ge-ctl"><label class="site-pick" for="gl-site">Site <select id="gl-site" data-act="gl-site">${options}</select></label>${site === "all" ? "" : `<a class="prim" href="#/gloves/${esc(site)}/enter">Enter Gloves</a>`}</div>
    <div class="box"><div class="cnt cnt4">${cells}</div></div>
    ${bucket ? `<div class="note">Showing ${esc(BUCKETS.find((b) => b.key === bucket)!.label)} only. <a class="lnk" href="#/gloves/${site}">Show Everyone</a></div>` : ""}
    <div class="box">${rows || '<div class="empty">Nobody here.</div>'}</div>
    <p class="ro">Worst first. The app retires pairs on their dates each night.</p>`;
}

export function gloveRecord(r: RosterRow, pg: PersonGloves, siteName: string, gloveSize: string | null): string {
  const card = r.card;
  const cardRows = card ? card.rows.map((x) => row(x.label, x.mono ? `<span class="mono">${esc(x.value)}</span>` : `<span class="${x.tone ? `gl-text-${x.tone}` : ""}">${esc(x.value)}</span>`)).join("") : "";
  const last = pg.inspections[0];
  const past = pg.pastPairs.length ? pg.pastPairs.map((p) => `<div>${esc(pastPairLine(p))}</div>`).join("") : '<span class="muted">None</span>';
  return `<a class="back" href="#/gloves/${esc(r.siteNumber)}">Gloves</a>
    ${ident(r.qewId.slice(-2), r.name, `${r.qewId} · ${siteName}`)}
    ${card?.retiredLine ? `<div class="note red">${esc(card.retiredLine)}</div>` : ""}
    <div class="box">
      <div class="boxhd"><div class="nm"><b>${r.pairId ? "Current Pair" : "Gloves"}</b><small>${esc(GLOVE_CLASS_LINE)}</small></div>${statusPill(r)}</div>
      <div class="rows">
        ${r.locked ? row("Status", "Gloves open once Nodes 1 to 3 are done.") : ""}
        ${cardRows}
        ${card?.rows.some((x) => x.label === "Size" || x.label === "Class · size") ? "" : row("Size", gloveSize ? esc(gloveSize) : '<span class="muted">Not picked</span>')}
        ${row("Inspections logged", pg.inspections.length ? `${pg.inspections.length} · last ${usDate(last!.inspectedAt.slice(0, 10))}${last!.jspNumber ? `, ${esc(last!.jspNumber)}` : ""}` : '<span class="muted">None yet. The JSP logs them (Stage 4).</span>')}
        ${row("Past pairs", past)}
      </div>
    </div>
    ${card?.note && card.note.tone !== "plain" ? `<div class="note${card.note.tone === "red" ? " red" : ""}">${esc(card.note.text)}</div>` : ""}
    <p class="ro">Read only. ${esc(r.name.split(" ")[0] ?? "The QEW")} records gloves on the glove card from the bag tag.</p>`;
}

// ---- Enter Gloves (GLOVES_ENTER_WORDS.md, MOCKUP_GLOVES_ENTER.html, approved 2026-10-24) ----

export interface EnterView {
  site: string;
  siteName: string;
  siteNames: Record<string, string>;
  rows: EnterRow[];
  cells: Record<string, EnterCell>;
  /** Per row: why it did not save. */
  errors: Record<string, string>;
  /** Per row: saved this time, in words. */
  saved: Record<string, string>;
  busy: boolean;
  /** The row number just double-tapped, lit for a moment. */
  hit: string;
}

/** One sheet per site, laid out like the workbook: the two cells to fill are Size and Test Date. */
export function glovesEnter(v: EnterView): string {
  const options = Object.keys(v.siteNames)
    .sort()
    .map((s) => `<option value="${esc(s)}"${s === v.site ? " selected" : ""}>${esc(`${s} · ${v.siteNames[s]}`)}</option>`)
    .join("");
  const n = v.rows.filter((r) => r.state === "open" && isFilled(v.cells[r.qewId])).length;
  const body = v.rows
    .map((r, i) => {
      const who = `<td class="ge-who"><span class="chip">${esc(r.qewId)}</span><b>${esc(r.name)}</b></td>`;
      const saved = v.saved[r.qewId];
      if (r.state !== "open" || saved) {
        return `<tr class="ge-off"><td class="ge-rn">${i + 1}</td>${who}<td></td><td></td><td class="ge-file${saved ? " ok" : ""}">${esc(saved ?? r.onFile)}</td></tr>`;
      }
      const c = v.cells[r.qewId] ?? { size: r.size, testedOn: "" };
      const sizes = ["", ...GLOVE_SIZES].map((s) => `<option value="${s}"${s === c.size ? " selected" : ""}>${s}</option>`).join("");
      const err = v.errors[r.qewId] ? `<small class="ge-err">${esc(v.errors[r.qewId]!)}</small>` : "";
      return `<tr><td class="ge-rn${v.hit === r.qewId ? " hit" : ""}" data-ge-rn="${esc(r.qewId)}" title="Double-tap to copy the test date down">${i + 1}</td>${who}
        <td><select class="ge-size${c.size ? "" : " need"}" data-ge-size="${esc(r.qewId)}" aria-label="Size for ${esc(r.name)}"${v.busy ? " disabled" : ""}>${sizes}</select></td>
        <td><input type="date" class="ge-date${c.testedOn ? "" : " need"}" data-ge-date="${esc(r.qewId)}" value="${esc(c.testedOn)}" aria-label="Test date for ${esc(r.name)}"${v.busy ? " disabled" : ""}></td>
        <td class="ge-file">${esc(r.onFile)}${err}</td></tr>`;
    })
    .join("");
  return `${ident("GL", `Enter Gloves · ${v.siteName}`, `${v.rows.length} active ${v.rows.length === 1 ? "QEW" : "QEWs"} · ${GLOVE_CLASS_LINE}`)}
    <label class="site-pick" for="ge-site">Site <select id="ge-site" data-act="ge-site"${v.busy ? " disabled" : ""}>${options}</select></label>
    <div class="note">Every pair goes in sealed. Each QEW opens the bag and taps Place In Service from the bag tag, which starts the 6-month clock that day.</div>
    <div class="box">
      <div class="ge-scroll"><table class="ge"><thead><tr><th></th><th>QEW</th><th>Size</th><th>Test Date</th><th>On File</th></tr></thead><tbody>${body || '<tr><td colspan="5" class="empty">No Active QEWs here.</td></tr>'}</tbody></table></div>
      <div class="ge-foot"><span>Double-tap a row number to copy its test date down to the empty rows below.</span>
        <span class="ge-btns"><a class="chg" href="#/gloves/${esc(v.site)}">Cancel</a><button class="prim" type="button" data-act="ge-save"${n && !v.busy ? "" : " disabled"}>${v.busy ? "Saving…" : saveLabel(n)}</button></span></div>
    </div>`;
}
